/**
 * The bundle-side copy of the "is this a stale / missing chunk?" pattern.
 *
 * One pattern, three consumers — ErrorBoundary (should this render the calm
 * loader and recover?), crashLogger (is this crash a stale chunk?) and
 * lazyChunk (should a rejected import be re-thrown in chunk shape?) — and a
 * fourth copy that CANNOT import this file: the inline boot script in
 * index.html, which is a classic script and runs before the bundle exists.
 * `src/__tests__/lazyChunk.test.js` pins the inline copy to this one.
 *
 * DELIBERATELY IMPORT-FREE. crashLogger loads Firebase; if lazyChunk took the
 * pattern from there, every suite that mocks crashLogger down to
 * `logCrashError` would fail on a missing export, and lazyChunk's own tests
 * would need a Firebase mock for a regex.
 *
 * The shapes, and why each is here:
 *   • `Loading chunk … failed` — webpack's wording. Kept after the move to Vite
 *     because lazyChunk still manufactures it (for a hung import, and for a
 *     native rejection it recognised), and the whole recovery chain keys on it.
 *   • `Failed to fetch dynamically imported module: <url>` — Chrome / Edge.
 *   • `error loading dynamically imported module: <url>` — Firefox.
 *   • `Importing a module script failed.` — Safari / iOS. NO URL in it.
 *   • `'text/html' is not a valid JavaScript MIME type` — Safari / iOS when a
 *     module script request came back as HTML: a poisoned HTTP-cache entry, or
 *     the Hosting `**` → /index.html rewrite answering for a chunk the deploy
 *     no longer serves. The service worker turns that HTML into a 404 while it
 *     controls the page, but the recovery unregisters every SW, so the page
 *     that reloads after a recovery sees the raw MIME error. NO URL either.
 *
 * What is NOT here: `Unexpected token '<'`. A module script that receives HTML
 * fails with the MIME error above, not a syntax error; crashLogger keeps its
 * own filename-bound `Unexpected token '<'` branch for the classic-script case.
 */

/** Matches the message of a chunk that failed to load — any browser, either bundler era. */
export const STALE_CHUNK_MESSAGE = /Loading chunk .* failed|Loading CSS chunk .* failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/;

/**
 * The two Safari shapes: a chunk error that names NO URL. A recovery for one of
 * these cannot heal the one poisoned entry, so it heals every `/static/js/`
 * resource the page knows about instead (`healAll` in index.html).
 */
export const URLLESS_CHUNK_MESSAGE = /Importing a module script failed|is not a valid JavaScript MIME type/;

/** Pull the failed asset URL out of a chunk error message, when the browser put one there. */
export const CHUNK_URL_IN_MESSAGE = /https?:\/\/[^\s)'"]+\.(?:js|css)/;

export function isStaleChunkMessage(msg) {
  return STALE_CHUNK_MESSAGE.test(String(msg || ""));
}

/** True for a recognised chunk error that carries no URL (the Safari shapes) — the `healAll` trigger. */
export function isUrllessChunkMessage(msg) {
  const s = String(msg || "");
  return URLLESS_CHUNK_MESSAGE.test(s) && !CHUNK_URL_IN_MESSAGE.test(s);
}
